import { ContractError } from './client'

/**
 * Turning `unknown` into types, one field at a time.
 *
 * Everything below throws `ContractError` rather than coercing. A missing
 * `version` that silently became `0` would be sent back to the server as an
 * approve of version zero, which the optimistic lock would refuse with a message
 * about a race that never happened — and nobody would find the real cause. A
 * loud failure at the boundary is the cheap version of that afternoon.
 */

export function asObject(value: unknown, what: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ContractError(`${what} is ${value === null ? 'null' : typeof value}, expected an object`)
  }
  return value as Record<string, unknown>
}

export function asArray(value: unknown, what: string): readonly unknown[] {
  if (!Array.isArray(value)) {
    throw new ContractError(`${what} is not an array`)
  }
  return value
}

export function str(source: Record<string, unknown>, name: string): string {
  const value = source[name]
  if (typeof value !== 'string') {
    throw new ContractError(`"${name}" is ${typeof value}, expected string`)
  }
  return value
}

/** A field the server omits when it has nothing to say. Empty string counts as nothing. */
export function optStr(source: Record<string, unknown>, name: string): string | undefined {
  const value = source[name]
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value !== 'string') {
    throw new ContractError(`"${name}" is ${typeof value}, expected string`)
  }
  return value
}

/** A field the server omits, read as an empty string. For `omitempty` prose. */
export function text(source: Record<string, unknown>, name: string): string {
  return optStr(source, name) ?? ''
}

export function num(source: Record<string, unknown>, name: string): number {
  const value = source[name]
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new ContractError(`"${name}" is ${typeof value}, expected a number`)
  }
  return value
}

export function optNum(source: Record<string, unknown>, name: string): number | undefined {
  const value = source[name]
  if (value === undefined || value === null) return undefined
  return num(source, name)
}

/** A counter Go omits when it is zero. */
export function count(source: Record<string, unknown>, name: string): number {
  return optNum(source, name) ?? 0
}

export function bool(source: Record<string, unknown>, name: string): boolean {
  const value = source[name]
  if (value === undefined) return false
  if (typeof value !== 'boolean') {
    throw new ContractError(`"${name}" is ${typeof value}, expected a boolean`)
  }
  return value
}

/**
 * A three-state flag: told yes, told no, never asked.
 *
 * `undefined` is not `false` here for the same reason unknown money is not zero
 * (invariant 2): "does not need a private room" and "nobody asked about a
 * private room" are different facts, and flattening them loses the one a manager
 * would act on.
 */
export function optBool(source: Record<string, unknown>, name: string): boolean | undefined {
  const value = source[name]
  if (value === undefined || value === null) return undefined
  if (typeof value !== 'boolean') {
    throw new ContractError(`"${name}" is ${typeof value}, expected a boolean`)
  }
  return value
}

/** Timestamps stay as the ISO strings the server sent; only the screen formats them. */
export function optTime(source: Record<string, unknown>, name: string): string | undefined {
  return optStr(source, name)
}

export function time(source: Record<string, unknown>, name: string): string {
  return str(source, name)
}

export function strings(source: Record<string, unknown>, name: string): readonly string[] {
  const value = source[name]
  if (value === undefined || value === null) return []
  const entries = asArray(value, `"${name}"`)
  return entries.map((entry) => {
    if (typeof entry !== 'string') {
      throw new ContractError(`"${name}" contains a non-string`)
    }
    return entry
  })
}

export function objects(source: Record<string, unknown>, name: string): readonly Record<string, unknown>[] {
  const value = source[name]
  if (value === undefined || value === null) return []
  return asArray(value, `"${name}"`).map((entry, index) => asObject(entry, `"${name}[${index}]"`))
}

/** JSON the server sends through untyped: risk flags, task payloads, field values. */
export function raw(source: Record<string, unknown>, name: string): unknown {
  return source[name]
}
